import React from 'react'
import PropTypes from 'prop-types'
import { Button, TextField } from '../../../group_leader/components/common'
import { makeT } from '../../../group_leader/helpers/i18n'

// Purse and points per place, with a real <th scope="row"> label; the two arrays stay paired.

const t = makeT('group_leader.event_setup.payout_table')

const PayoutTable = ({
  labels,
  payoutRows,
  pointsRows,
  setPayoutRows,
  setPointsRows,
  isExpandable,
  idPrefix,
  perPlayer,
  showPoints = true,
  currencySymbol,
}) => {
  const visibleLabels = isExpandable ? labels.slice(0, payoutRows.length) : labels

  // In per-player mode a single-place payout (winner-takes-all) is the pot the whole
  // entry-fee pool pays out, so its row reads "Entry fee" rather than the server's "Pot".
  const singlePlacePerPlayer = perPlayer && visibleLabels.length === 1
  const placeLabel = (label) => (singlePlacePerPlayer ? t('entry_fee_place') : label)

  const setRow = (setter) => (index, value) => setter((prev) => {
    const next = [ ...prev ]
    // Grown to reach the index: the rows start at three while the server can declare more places,
    // and writing past the end left holes that serialize as null.
    while (next.length <= index) { next.push('') }
    next[index] = value
    return next
  })
  const setPayoutAt = setRow(setPayoutRows)
  const setPointsAt = setRow(setPointsRows)

  const addPlace = () => {
    setPayoutRows((prev) => [ ...prev, '' ])
    setPointsRows((prev) => [ ...prev, '' ])
  }
  const removeLastPlace = () => {
    setPayoutRows((prev) => prev.slice(0, -1))
    setPointsRows((prev) => prev.slice(0, -1))
  }

  return (
    <>
      <table className="gl-event-setup-payout-table">
        <thead>
          <tr>
            <th className="gl-event-setup-payout-th" scope="col">
              <span className="sr-only">{t('place_header')}</span>
            </th>
            <th className="gl-event-setup-payout-th" scope="col">{t('purse_header', { currency: currencySymbol })}</th>
            {showPoints && (
              <th className="gl-event-setup-payout-th" scope="col">{t('points_header')}</th>
            )}
            {isExpandable && (
              <th className="gl-event-setup-payout-th" scope="col">
                <span className="sr-only">{t('remove_header')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {visibleLabels.map((label, i) => {
            // One label for the row header and both inputs: in per-player mode they disagreed.
            const rowLabel = placeLabel(label)

            return (
              <tr key={`${idPrefix}-${i}`}>
                <th className="gl-event-setup-payout-place" scope="row">{rowLabel}</th>
                <td className="gl-event-setup-payout-cell">
                  <TextField
                    id={`${idPrefix}-purse-${i}`}
                    type="number"
                    min="0"
                    step="0.01"
                    prefix={currencySymbol}
                    ariaLabel={t('purse_aria_label', { label: rowLabel })}
                    placeholder={t('purse_placeholder')}
                    value={payoutRows[i] || ''}
                    onChange={(value) => setPayoutAt(i, value)}
                  />
                </td>
                {showPoints && (
                  <td className="gl-event-setup-payout-cell">
                    <TextField
                      id={`${idPrefix}-points-${i}`}
                    type="number"
                    min="0"
                    step="1"
                    ariaLabel={t('points_aria_label', { label: rowLabel })}
                    placeholder={t('points_placeholder')}
                      value={pointsRows[i] || ''}
                      onChange={(value) => setPointsAt(i, value)}
                    />
                  </td>
                )}
                {isExpandable && (
                  <td className="gl-event-setup-payout-cell">
                    {i === visibleLabels.length - 1 && payoutRows.length > 1 && (
                      <button
                        type="button"
                        className="gl-event-setup-payout-rm"
                        onClick={removeLastPlace}
                        aria-label={t('remove_place_aria', { label: rowLabel })}
                      >
                        &times;
                      </button>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>

      {isExpandable && payoutRows.length < labels.length && (
        <div className="gl-event-setup-payout-btns">
          <Button variant="secondary" size="sm" onClick={addPlace}>{t('add_place')}</Button>
        </div>
      )}
    </>
  )
}

PayoutTable.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  payoutRows: PropTypes.arrayOf(PropTypes.string).isRequired,
  pointsRows: PropTypes.arrayOf(PropTypes.string).isRequired,
  setPayoutRows: PropTypes.func.isRequired,
  setPointsRows: PropTypes.func.isRequired,
  // Ranked payouts (10+ places) start collapsed and grow a place at a time
  isExpandable: PropTypes.bool,
  // Distinguishes the two panels' inputs, which are on the page at different times
  idPrefix: PropTypes.string.isRequired,
  // Per-player entry-fee mode ('dynamic'): relabels a single-place (winner-takes-all)
  // row as "Entry fee", since the number entered is a fee each golfer pays, not a pot.
  perPlayer: PropTypes.bool,
  // The pro-rata point payouts award no points of their own, so their column is not offered.
  showPoints: PropTypes.bool,
  // The league's currency symbol, not a per-locale constant.
  currencySymbol: PropTypes.string.isRequired,
}

export default PayoutTable
